import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { MagnifyingGlass, Buildings, CheckCircle, ShieldCheck, Warning } from "@phosphor-icons/react";

const Tag = ({ ok, children }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${ok === null ? "bg-slate-100 text-slate-600" : ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
    {ok === null ? null : ok ? <ShieldCheck size={12} weight="fill" /> : <Warning size={12} weight="fill" />}{children}
  </span>
);
const insLabel = (k) => { const n = Number(k) * 1000; return n >= 1e6 ? `$${(n / 1e6).toLocaleString(undefined, { maximumFractionDigits: 2 })}M` : `$${n.toLocaleString()}`; };

function ResultCard({ r, active, onPick, i, testid = "fmcsa" }) {
  const ins = Number(r.bipd_on_file) > 0;
  return (
    <button type="button" onClick={() => onPick(r)} data-testid={`${testid}-result-${i}`}
      className={`w-full text-left rounded-xl border p-3.5 transition-colors ${active ? "border-emerald-500 bg-emerald-500/10 ring-1 ring-emerald-500/40" : "border-slate-200 bg-white hover:border-blue-400"}`}>
      <div className="flex items-start gap-3">
        <Buildings size={22} weight="duotone" className="text-blue-600 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="font-heading font-bold text-slate-900 truncate">{r.legal_name}</p>
            {active && <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0" />}
          </div>
          {r.dba_name && <p className="text-xs text-slate-500">DBA {r.dba_name}</p>}
          <p className="text-xs text-slate-600 mt-0.5">{r.address || "No address on file"}</p>
          <p className="text-[11px] font-mono text-slate-500 mt-1">USDOT {r.usdot || "—"}{r.mc_number ? ` · ${r.mc_number}` : ""}{r.phone ? ` · ${r.phone}` : ""}{r.email ? ` · ${r.email}` : ""}</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            <Tag ok={r.status === "Authorized"}>{r.status}</Tag>
            <Tag ok={ins}>{ins ? `Insurance ${insLabel(r.bipd_on_file)}` : "No insurance on file"}</Tag>
            <Tag ok={r.safety_rating === "Satisfactory" ? true : r.safety_rating === "Not Rated" || !r.safety_rating ? null : false}>{r.safety_rating || "Not Rated"}</Tag>
            {r.power_units != null && <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600">{r.power_units} trucks · {r.drivers ?? 0} drivers</span>}
          </div>
        </div>
      </div>
    </button>
  );
}

export function FmcsaLookup({ onSelect, initialKind = "mc", testid = "fmcsa" }) {
  const [kind, setKind] = useState(initialKind);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [msg, setMsg] = useState("");
  const search = async () => {
    if (!q.trim()) return;
    setBusy(true); setMsg(""); setResults([]);
    try {
      const { data } = await api.get("/fmcsa/search", { params: { kind, q } });
      if (data.ok) { setResults(data.results); if (data.results.length === 1) pick(data.results[0]); }
      else setMsg(data.message);
    } catch (e) { setMsg(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const pick = (r) => { setPicked(r); onSelect(r); };
  return (
    <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-3.5" data-testid={testid}>
      <p className="text-xs font-bold uppercase tracking-wide text-blue-700 mb-2">Find company in FMCSA</p>
      <div className="flex gap-2">
        <div className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5 shrink-0">
          {[["mc", "MC #"], ["usdot", "USDOT #"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setKind(k)} data-testid={`${testid}-kind-${k}`} className={`h-8 px-3 rounded-md text-xs font-semibold transition-colors ${kind === k ? "bg-slate-900 text-white" : "text-slate-600"}`}>{l}</button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
          placeholder={kind === "mc" ? "Enter MC number" : "Enter USDOT number"} className="flex-1 min-w-0 h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm font-mono" data-testid={`${testid}-input`} />
        <button type="button" onClick={search} disabled={busy} className="h-9 px-4 rounded-lg bg-blue-600 text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-blue-700 disabled:opacity-50 shrink-0" data-testid={`${testid}-search`}>
          <MagnifyingGlass size={15} weight="bold" />{busy ? "Searching…" : "Search"}
        </button>
      </div>
      {msg && <p className="mt-2 text-xs text-amber-700" data-testid={`${testid}-msg`}>{msg}</p>}
      {results.length > 0 && (
        <div className="mt-3 space-y-2 max-h-80 overflow-y-auto" data-testid={`${testid}-results`}>
          {results.length > 1 && <p className="text-xs text-slate-500">{results.length} companies found. Select the right one:</p>}
          {results.map((r, i) => <ResultCard key={`${r.usdot}-${i}`} r={r} i={i} active={picked === r} onPick={pick} testid={testid} />)}
        </div>
      )}
    </div>
  );
}
